import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Check } from "lucide-react";

const EASE = [0.2, 0.8, 0.2, 1];

const Swatch = ({ hex, name }) => (
  <div className="flex items-center gap-2">
    <span className="h-6 w-6 rounded-full border border-[rgba(244,239,230,0.2)]" style={{ background: hex }} />
    <span className="text-xs text-[rgba(244,239,230,0.6)]">{name}</span>
  </div>
);

/* Mini CSS mockups — each renders a tiny homepage comp in that direction's style */

const MockupDark = () => (
  <div className="rounded-xl overflow-hidden border border-[rgba(244,239,230,0.15)]" style={{ background: "#0E0F10" }}>
    <div className="px-4 py-2 flex items-center justify-between border-b" style={{ borderColor: "rgba(244,239,230,0.12)" }}>
      <span className="text-[9px] tracking-[0.2em] uppercase" style={{ color: "#B89A63" }}>LA&A</span>
      <span className="text-[8px] px-2 py-0.5 rounded" style={{ background: "#F4EFE6", color: "#0E0F10" }}>Request</span>
    </div>
    <div className="p-4">
      <p className="font-serif-display text-[15px] leading-tight" style={{ color: "#F4EFE6" }}>
        Appraisals & authentication for the world's finest goods.
      </p>
      <div className="mt-2 h-1 w-10" style={{ background: "#B89A63" }} />
      <div className="mt-3 grid grid-cols-3 gap-1.5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-10 rounded border" style={{ borderColor: "rgba(244,239,230,0.12)", background: "rgba(20,21,23,0.7)" }} />
        ))}
      </div>
    </div>
  </div>
);

const MockupLight = () => (
  <div className="rounded-xl overflow-hidden border border-[rgba(20,20,20,0.15)]" style={{ background: "#FBF7F0" }}>
    <div className="px-4 py-2 flex items-center justify-between border-b" style={{ borderColor: "rgba(20,20,20,0.1)" }}>
      <span className="text-[9px] tracking-[0.2em] uppercase" style={{ color: "#8A6A3A" }}>LA&A</span>
      <span className="text-[8px] px-2 py-0.5 rounded" style={{ background: "#141414", color: "#FBF7F0" }}>Request</span>
    </div>
    <div className="p-4">
      <p className="font-serif-display text-[15px] leading-tight" style={{ color: "#141414" }}>
        Quiet expertise for exceptional objects.
      </p>
      <div className="mt-2 h-px w-16" style={{ background: "#8A6A3A" }} />
      <div className="mt-3 grid grid-cols-3 gap-1.5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-10 rounded border" style={{ borderColor: "rgba(20,20,20,0.1)", background: "#fff" }} />
        ))}
      </div>
    </div>
  </div>
);

const MockupBold = () => (
  <div className="rounded-xl overflow-hidden border border-[rgba(244,239,230,0.15)]" style={{ background: "#0E0F10" }}>
    <div className="px-4 py-2 flex items-center justify-between border-b" style={{ borderColor: "rgba(244,239,230,0.12)" }}>
      <span className="text-[9px] tracking-[0.2em] uppercase" style={{ color: "#F4EFE6" }}>LA&A</span>
      <span className="text-[8px] px-2 py-0.5 rounded" style={{ background: "#4B5B3A", color: "#F4EFE6" }}>Request</span>
    </div>
    <div className="p-4">
      <p className="font-serif-display text-[20px] leading-[0.95] uppercase" style={{ color: "#F4EFE6" }}>
        Real. Or it isn't.
      </p>
      <div className="mt-3 flex gap-1.5">
        <div className="h-12 w-2/3 rounded" style={{ background: "#4B5B3A" }} />
        <div className="h-12 w-1/3 rounded border" style={{ borderColor: "rgba(244,239,230,0.2)" }} />
      </div>
    </div>
  </div>
);

const DIRECTIONS = [
  {
    key: "dark-editorial",
    name: "Old-Money Dark Editorial",
    status: "Built — Live Now",
    active: true,
    mockup: <MockupDark />,
    typography: "Gloock (display serif) + IBM Plex Sans",
    swatches: [
      ["#0E0F10", "Near-black ink"],
      ["#F4EFE6", "Warm ivory"],
      ["#B89A63", "Champagne"],
    ],
    notes: [
      "Museum-placard cards with hairline borders — no heavy shadows",
      "Magazine-cover hero with large serif headline",
      "Champagne accent reserved for CTAs, labels & focus states",
      "Quiet motion: fades and subtle lifts, auction-house calm",
    ],
    rationale:
      "Reads like a Sotheby's catalog — credential-forward, high-contrast, and unmistakably premium. The strongest fit for expert-witness credibility and high-net-worth clientele.",
  },
  {
    key: "modern-premium",
    name: "Modern Premium (Light / Minimal)",
    status: "Alternate Concept",
    active: false,
    mockup: <MockupLight />,
    typography: "Light-weight serif display + neutral grotesque",
    swatches: [
      ["#FBF7F0", "Warm white"],
      ["#141414", "Ink"],
      ["#8A6A3A", "Muted bronze"],
    ],
    notes: [
      "Warm-white gallery aesthetic with generous whitespace",
      "Hairline dividers instead of cards; content breathes",
      "Deep-ink CTAs with minimal shadow",
      "Feels like a modern gallery or private atelier site",
    ],
    rationale:
      "Approachable and contemporary — suits a broader consumer audience and reads beautifully on mobile. Slightly less 'authority' than the dark direction, but excellent for conversion-focused landing pages.",
  },
  {
    key: "bold-editorial",
    name: "Contemporary Bold Editorial",
    status: "Alternate Concept",
    active: false,
    mockup: <MockupBold />,
    typography: "Oversized display serif, tight leading, uppercase moments",
    swatches: [
      ["#0E0F10", "Near-black"],
      ["#F4EFE6", "Ivory"],
      ["#4B5B3A", "Deep olive"],
    ],
    notes: [
      "High-contrast asymmetric grid with oversized type",
      "One strong accent (deep olive) used sparingly",
      "Kinetic scroll reveals and marquee brand rows",
      "Editorial confidence — still luxury, never loud",
    ],
    rationale:
      "A statement direction for a brand that wants to feel like the future of authentication — memorable and press-worthy, best if targeting resale platforms and younger collectors.",
  },
];

export default function DesignOptions() {
  return (
    <div className="min-h-screen bg-[color:var(--laa-ink)] text-[color:var(--laa-ivory)]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <Link to="/" data-testid="design-options-back-link" className="inline-flex items-center gap-2 text-sm text-[rgba(244,239,230,0.6)] hover:text-[color:var(--laa-ivory)] laa-link">
          <ArrowLeft size={15} /> Back to site
        </Link>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, ease: EASE }}>
          <p className="laa-label mt-10">Design Exploration</p>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.02em]">
            Three directions for Luxury-Appraisals.com
          </h1>
          <p className="mt-5 max-w-2xl text-sm sm:text-base leading-relaxed text-[rgba(244,239,230,0.65)]">
            The primary direction — <span className="text-[color:var(--laa-ivory)]">Old-Money Dark Editorial</span> — has been
            fully built and is live on the homepage. The two alternates below are presented at concept level:
            palette, typography, and layout language, ready to be built out if preferred.
          </p>
        </motion.div>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-3 gap-6">
          {DIRECTIONS.map((d, i) => (
            <motion.div
              key={d.key}
              data-testid={`design-option-${d.key}`}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.1 + i * 0.1, ease: EASE }}
              className={`rounded-2xl border p-6 flex flex-col ${
                d.active
                  ? "border-[color:var(--laa-champagne)] bg-[rgba(184,154,99,0.05)]"
                  : "border-[color:var(--laa-hairline)] bg-[rgba(20,21,23,0.55)]"
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <Badge
                  className={`rounded-full text-[10px] tracking-[0.14em] uppercase ${
                    d.active
                      ? "bg-[color:var(--laa-champagne)] text-[color:var(--laa-ink)] hover:bg-[color:var(--laa-champagne)]"
                      : "bg-[rgba(244,239,230,0.08)] text-[rgba(244,239,230,0.6)] hover:bg-[rgba(244,239,230,0.08)]"
                  }`}
                >
                  {d.status}
                </Badge>
              </div>
              <h2 className="mt-4 text-xl leading-tight">{d.name}</h2>
              <div className="mt-5">{d.mockup}</div>
              <div className="mt-5 space-y-1.5">
                <p className="laa-label">Palette</p>
                <div className="flex flex-wrap gap-x-5 gap-y-2 pt-1">
                  {d.swatches.map(([hex, name]) => (
                    <Swatch key={hex + name} hex={hex} name={name} />
                  ))}
                </div>
              </div>
              <div className="mt-5">
                <p className="laa-label">Typography</p>
                <p className="mt-1.5 text-xs text-[rgba(244,239,230,0.65)]">{d.typography}</p>
              </div>
              <ul className="mt-5 space-y-2">
                {d.notes.map((n) => (
                  <li key={n} className="flex gap-2 text-xs leading-relaxed text-[rgba(244,239,230,0.6)]">
                    <Check size={13} className="text-[color:var(--laa-champagne)] shrink-0 mt-0.5" />
                    {n}
                  </li>
                ))}
              </ul>
              <p className="mt-5 pt-5 border-t border-[color:var(--laa-hairline)] text-xs leading-relaxed text-[rgba(244,239,230,0.55)] italic">
                {d.rationale}
              </p>
              {d.active && (
                <Link to="/" className="mt-6">
                  <Button
                    data-testid="design-option-view-live-button"
                    className="w-full rounded-xl bg-[color:var(--laa-ivory)] text-[color:var(--laa-ink)] hover:bg-white"
                  >
                    View the live build
                  </Button>
                </Link>
              )}
            </motion.div>
          ))}
        </div>

        <p className="mt-12 text-xs text-[rgba(244,239,230,0.4)]">
          Want one of the alternates built out instead — or a hybrid? Any direction can be applied to the existing
          structure without rebuilding functionality.
        </p>
      </div>
    </div>
  );
}
